import message from "@/utils/globalMessage";
import React from 'react';
import { api } from "@/redux/api";
import { useSafeState } from "ahooks";
import { Empty, Upload } from "antd";
import { useEffect } from "react";
import { formatEquipmentRef, formatWorkorderRef } from "@/utils/entity-ref";

/**
 * FileManagement 的 API 适配器机制（2026-10-05）：
 * 组件通过 props.fileApi 传入替代实现（如承包商门户的 /api/v2/contract/
 * 镜像接口），挂载时调用 setFileManagementApiOverride 注册；本模块内的
 * 模块级工具函数（usePreviewUrl/copyLink/handleDownload/openInBrowser/
 * openInApp）与 FormItemTag/ImageView 一律经 getFileManagementApi() 取
 * 当前生效的 API——未注册时回退主门户 api，行为与改造前完全一致。
 */
let fileManagementApiOverride = null;
export const setFileManagementApiOverride = (override) => {
  fileManagementApiOverride = override || null;
};
export const getFileManagementApi = () => fileManagementApiOverride || api;

/** 主布局侧栏为 `position: fixed` 且 z-index ~1201；文件管理相关浮层挂载在 body 上须高于侧栏。 */
export const FILE_MANAGEMENT_POPUP_Z_INDEX = 1310;

/** 删除确认对话框须高于下拉菜单/Popconfirm（1310），且高于预览 Drawer（1400）。 */
export const FILE_MANAGEMENT_DELETE_CONFIRM_MODAL_Z_INDEX = 1460;

export const getFileExtension = (filename) => {
  return (filename || '').split('.').pop().toLowerCase();
}

export const getPathsByCurrentTree = (currentTreeKey) => {
  let arr = [];
  let cur = currentTreeKey;

  while (cur) {
    arr.unshift(cur);
    cur = cur?.parentFolder;
  }

  return arr
}

/**
 * 行级「所在目录」路径文案（列表模式的 Path 列）。
 *
 * 沿 parentFolder 回溯到根，去掉 root 段，用 ' / ' 连接。文件与目录口径
 * 一致：都返回「它所在的那个目录」，不含自身。
 *   - 直接位于 root 下的行（含顶层自建目录）→ 'root'
 *   - parentFolder 缺失（父目录不在映射里）→ ''
 * 段名用 title（目录的 display_name || name），与面包屑 / H5 一致。
 */
export const getNodeLocationPath = (node, separator = ' / ') => {
  const parent = node?.parentFolder;
  if (!parent) return '';
  const segments = getPathsByCurrentTree(parent)
    .filter(item => !item?.isRoot && item?.key !== 'root' && item?.id !== 'root')
    .map(item => item?.title || item?.name)
    .filter(Boolean);
  return segments.length ? segments.join(separator) : 'root';
}

/**
 * R1：节点是否位于 System 子树内（全局 /file-manage 模式的虚拟 System 目录
 * 自身 + 其下所有虚拟/真实子孙行）。沿 parentFolder 链向上，命中
 * key/id === 'system' 即 true；root 自身豁免。scoped 模式的树里没有
 * System 节点，恒为 false。
 */
export const isNodeInSystemTree = (node) => {
  let cur = node;
  while (cur) {
    if (cur.isRoot) return false;
    if (cur.key === 'system' || cur.id === 'system') return true;
    cur = cur.parentFolder;
  }
  return false;
}

/**
 * R1：文件管理模式。显式传 fileManageMode 优先；否则按 tableName === 'All'
 * 推导（老调用点零改动）。System 只读守卫只在 'global' 模式生效。
 */
export const getFileManageMode = (props) =>
  props?.fileManageMode || (props?.tableName === 'All' ? 'global' : 'scoped');

/** R1：仅全局模式下，System 子树目标只读（禁止 move/delete/create/upload/rename/edit tags 等一切写操作）。 */
export const isSystemReadonlyTarget = (props, node) =>
  getFileManageMode(props) === 'global' && isNodeInSystemTree(node);

// ---------------------------------------------------------------------------
// 实体过滤（按 equipment / workorder 过滤文件）
//
// 口径：把选中实体折算成一组「目录标题」关键字（EQ-11 / WO-1 / WO-2 ...），
// 在全部 folders + files 里按**文件夹标题精确匹配**。刻意不用 includes：
// EQ-1 会误命中 EQ-11 / EQ-110，WO-1 会误命中 WO-12。
// ---------------------------------------------------------------------------

export const normalizeEntityKeyword = (value) =>
  String(value ?? '').trim().toUpperCase();

export const buildEntityKeywordSet = (keywords = []) =>
  new Set(
    (keywords || []).map(normalizeEntityKeyword).filter(Boolean)
  );

/** 实体过滤：只匹配文件夹，且标题必须与某个关键字完全相等。 */
export const matchEntityFilter = (item, keywordSet) => {
  if (!item || !item.isFolder || !keywordSet || keywordSet.size === 0) {
    return false;
  }
  return keywordSet.has(normalizeEntityKeyword(item.title ?? item.name));
};

const collapseWhitespace = (value) =>
  String(value ?? '').replace(/\s+/g, ' ').trim();

/**
 * equipment 的展示口径：主标题用设备名，编号 EQ-{id} 降为副标题。
 * name 为空时回退编号（此时不显示副标题，避免两行重复）。
 */
export const getEquipmentDisplay = (equipment) => {
  const ref = formatEquipmentRef(equipment?.id);
  const name = collapseWhitespace(equipment?.name);
  return { primary: name || ref, secondary: name ? ref : '' };
};

/**
 * workorder 的展示口径：主标题优先 issue（工单问题描述），其次
 * workordernumber，最后才回退编号 WO-{id}；编号始终作为副标题。
 */
export const getWorkorderDisplay = (workorder) => {
  const ref = formatWorkorderRef(workorder?.id);
  const primary =
    collapseWhitespace(workorder?.issue) ||
    collapseWhitespace(workorder?.workordernumber) ||
    ref;
  return { primary, secondary: primary === ref ? '' : ref };
};

/** 过滤条件统一文案：Equipment Chiller 1 (EQ-11) / Workorder Pump leaking (WO-12)。 */
export const getEntityFilterTitle = (filter, withType = true) => {
  if (!filter) return '';
  const typeLabel = filter.type === 'equipment' ? 'Equipment' : 'Workorder';
  const primary =
    collapseWhitespace(filter.displayName || filter.name) || filter.label;
  const body =
    primary && primary !== filter.label
      ? `${primary} (${filter.label})`
      : primary || filter.label;
  return withType ? `${typeLabel} ${body}` : body;
};

/**
 * 结果列表里给「命中的虚拟实体目录（EQ-xx / WO-xx）」取两行标题：
 * 主标题为实体名，副标题为编号。
 *
 * 只对 isSystem / isVirtual 的虚拟目录生效——用户自建的真实目录即便
 * 恰好叫 EQ-11 也不会被装饰；同时依赖 filter.keywordInfo，没有该字段
 * （如旧载荷）时回退到原来的单行 title。
 */
export const resolveEntityRowDisplay = (filter, item) => {
  if (!filter?.keywordInfo || !item?.isFolder) return null;
  if (!item.isSystem && !item.isVirtual) return null;
  return (
    filter.keywordInfo[normalizeEntityKeyword(item.title ?? item.name)] || null
  );
};

const compareEntityNodes = (a, b) => {
  if (!!a.isFolder !== !!b.isFolder) return a.isFolder ? -1 : 1;
  return collapseWhitespace(a.title || a.name).localeCompare(
    collapseWhitespace(b.title || b.name),
    undefined,
    { numeric: true, sensitivity: 'base' }
  );
};

/**
 * 应用 Filter Files 后的结果集：从命中的目录出发，**先序**收集
 * 「自身 + 全部子孙文件夹 + 全部文件」，拍平成一维数组。
 *
 * - 父节点一定排在子节点之前（同一层文件夹优先、按标题自然序）；
 * - seen 去重，同时防住「匹配根互相嵌套」与异常环；
 * - 不修改共享的 nodes 数组，也不给节点写临时字段。
 */
export const collectEntityFilterNodes = (folders, keywordSet) => {
  if (!keywordSet || keywordSet.size === 0) return [];

  const roots = (folders || [])
    .filter((folder) => matchEntityFilter(folder, keywordSet))
    .sort(compareEntityNodes);

  const seen = new Set();
  const rows = [];
  const stack = roots.slice().reverse();

  while (stack.length) {
    const node = stack.pop();
    const key = node?.key || `${node?.isFolder ? 'f' : 'x'}-${node?.id}`;
    if (!node || !key || seen.has(key)) continue;
    seen.add(key);
    rows.push(node);

    if (!node.isFolder) continue;
    const children = (node.nodes || [])
      .filter(Boolean)
      .slice()
      .sort(compareEntityNodes);
    for (let i = children.length - 1; i >= 0; i -= 1) {
      stack.push(children[i]);
    }
  }

  return rows;
};

const FOLDER_KEY = 'folder-';
const FILE_KEY = 'file-';

/**
 * 构建文件管理左侧树。
 *
 * @param {Array} files 真实 KpiFiles 行
 * @param {Array} folders 真实 Folder 行
 * @param {string|undefined} id 期望选中的树节点 id
 * @param {object} props FileManagement 收到的 props
 */
export const formatFilesToTreeData = (files, folders, id, props) => {

  if (props.tableName === 'All') {
    return formatFilesToTreeDataWithGlobal(files, folders, id, props)
  }

  const cloneFiles = [...files];
  const cloneFolders = [...folders];
  const root = {
    isRoot: true,
    isFolder: true,
    parentFolder: null,
    isSystem: true,
    key: 'root',
    title: 'root',
    children: [],
    nodes: [],
    context: {
      table_name: props.tableName,
      equipmentId: props.equipmentId,
      record_id: props.recordId,
      canCreate: true,
      canUpload: true
    }
  }

  const FileMapping = new Map();

  for (const folder of cloneFolders) {
    const key = FOLDER_KEY + folder.id;
    folder.key = key;
    folder.isFolder = true;
    // display_name 由后端按锚定业务行（合同 title / 承包商公司名）实时派生，
    // name 始终是稳定 slug；展示一律走 title。
    folder.title = folder.display_name || folder.name;
    folder.children = [];
    folder.nodes = [];
    if (folder.is_system_managed) {
      // 与合同详情页附件同一套 channel 规则：新建（上传/复制粘贴）只有
      // Announcement 接受；移动（Move To / 剪切粘贴）Announcement 和
      // Submission 都接受；合同根目录仅作结构。
      folder.context = {
        canCreate: false,
        canUpload: folder.table_name === 'contract_note_announcement',
        canMove: folder.table_name !== 'contract_request',
        managed: true,
      };
    }
    FileMapping.set(key, folder)
  }

  for (const file of cloneFiles) {
    const key = FILE_KEY + file.id;
    file.key = key;
    file.isFolder = false;
    file.title = file.name;
    FileMapping.set(key, file)
  }


  for (const file of [...cloneFolders, ...cloneFiles]) {
    if (file.parent === null || file.folder === null) {
      if (file.isFolder) {
        root.children.push(file)
      }

      root.nodes.push(file);
      file.parentFolder = root;

    } else {

      const parentId = file.isFolder ? file.parent : file.folder;

      const key = FOLDER_KEY + parentId;
      if (FileMapping.has(key)) {
        const parent = FileMapping.get(key);
        if (file.isFolder) {
          parent.children.push(file);
        }
        parent.nodes.push(file);
        file.parentFolder = parent;
      }
    }
  }
  return [
    root,
    id ? cloneFolders.find(item => item.id === id) : root,
    {
      files: cloneFiles,
      folders: cloneFolders
    }
  ]
}

/**
 * 全局模式（`tableName === 'All'`，即 /file-manage 页面）的树构建。
 *
 * System 之下平级挂四类业务根（前三个是纯虚拟目录，最后一个是「虚拟分组根
 * + 真实 Folder」）：
 *   - Equipment          / {设备名}-{设备id}            table_name='equipment'
 *   - Workorder          / WO-{record_id}               table_name='workorder'
 *   - Functional Testing / {设备名}-{设备id} / {record_id}
 *                                                      table_name='functiontesting'
 *   - ContractRequest    / {id}-{title} / ...           table_name='contract_request'
 *     （其下的 {id}-{title} / Announcement / Submission - {公司名} 是真实 Folder
 *      记录，按 table_name='contract_request' 回挂到该虚拟根下，文件按 folder
 *      归属自然成树）
 *
 * 工单已是多设备（KpiWorkorder.equipments M2M），所以 Workorder 目录只按
 * record_id 建一次、不再按设备分桶；Equipment 根只收录真正有 equipment 附件
 * 的设备（工单 / 功能测试附件不会凭空造出设备目录）。Functional Testing 仍是
 * 单设备归属，因此其根下保留一层设备目录。
 *
 * 所有虚拟目录都在 System 子树内 → 全局模式一律只读（R1）。
 *
 * 详情页 Attachments Tab 走的是 scoped 分支（tableName !== 'All'），
 * 不经过本函数，因此完全不受影响。
 */
export const formatFilesToTreeDataWithGlobal = (files, folders, id, props) => {
  const cloneFiles = [...files];
  const cloneFolders = [...folders];

  // equipmentId -> Equipment/{设备名-id}（只由 table_name='equipment' 行驱动）
  const equipmentFolderMappings = {};
  // record_id -> Workorder/WO-{record_id}（工单是多设备 M2M，扁平化后天然去重）
  const workOrderFolderMappings = {};
  // record_id -> Functional Testing/{设备名-id}/{record_id}
  const functionalTestingFolderMappings = {};
  // equipmentId -> Functional Testing/{设备名-id}（FT 专用设备层，绝不复用 Equipment 根）
  const functionalTestingEquipmentFolderMappings = {};
  // 有真实 contract_request 目录时由 makeSystemRelationShip 创建并赋值
  let contractRequestRootNode = null;

  const root = {
    isRoot: true,
    isFolder: true,
    parentFolder: null,
    isSystem: true,
    key: 'root',
    id: 'root',
    title: 'root',
    children: [],
    nodes: [],
    context: {
      table_name: props.tableName,
      equipmentId: props.equipmentId,
      record_id: props.recordId,
      canCreate: true,
      canUpload: true
    }
  }

  const FileMapping = new Map();


  for (const folder of cloneFolders) {
    const key = FOLDER_KEY + folder.id;
    folder.key = key;
    folder.isFolder = true;
    // display_name 由后端按锚定业务行实时派生（合同 title / 承包商公司名），
    // name 始终是稳定 slug；展示一律走 title。
    folder.title = folder.display_name || folder.name;
    folder.children = [];
    folder.nodes = [];
    if (folder.is_system_managed) {
      // 与合同详情页附件同一套 channel 规则：新建（上传/复制粘贴）只有
      // Announcement 接受；移动（Move To / 剪切粘贴）Announcement 和
      // Submission 都接受；合同根目录仅作结构。
      folder.context = {
        canCreate: false,
        canUpload: folder.table_name === 'contract_note_announcement',
        canMove: folder.table_name !== 'contract_request',
        managed: true,
      };
    }
    FileMapping.set(key, folder)
  }

  for (const file of cloneFiles) {
    const key = FILE_KEY + file.id;
    file.key = key;
    file.isFolder = false;
    file.title = file.name;
    FileMapping.set(key, file)
  }

  for (const file of [...cloneFolders, ...cloneFiles]) {
    // 已经落在真实目录里的行不参与虚拟目录建模（Folder 行看 parent，
    // KpiFiles 行看 folder，另一个字段是 undefined 而非 null）。
    if (file.parent || file.folder) continue;

    if (file.table_name === 'equipment' && file.equipment_info) {
      makeEquipmentFolder(file.equipment_info)
    }

    // 工单是多设备（M2M），只按 record_id 建一次目录，不再依赖 equipment。
    if (file.table_name === 'workorder') {
      makeWorkOrderFolder(file.record_id)
    }

    if (file.table_name === 'functiontesting') {
      makeFunctionalTestingFolder(file.equipment_info, file.record_id)
    }
  }

  const systemFolders = makeSystemRelationShip(root);

  for (const file of [...cloneFolders, ...cloneFiles]) {
    if (file.parent === null || file.folder === null) {

      let currentParent = root;
      if (file.table_name === 'equipment' && file.equipment_info) {
        // record_id 是历史列，部分老数据为空，故以 equipment_info.id 为主
        currentParent = equipmentFolderMappings[file.equipment_info.id]
          || equipmentFolderMappings[file.record_id]
          || root;
      } else if (file.table_name === 'workorder') {
        // 一维映射：同一 record_id 的多设备附件自动汇入同一个 WO 目录
        currentParent = workOrderFolderMappings[file.record_id] || root;
      } else if (file.table_name === 'functiontesting') {
        currentParent = functionalTestingFolderMappings[file.record_id] || root;
      } else if (file.isFolder && file.table_name === 'contract_request' && contractRequestRootNode) {
        // 合同请求目录（真实 Folder）回挂到虚拟 ContractRequest 根下
        currentParent = contractRequestRootNode;
      }

      if (file.isFolder) {
        currentParent.children.push(file)
      }

      currentParent.nodes.push(file);
      file.parentFolder = currentParent;


    } else {

      const parentId = file.isFolder ? file.parent : file.folder;

      const key = FOLDER_KEY + parentId;
      if (FileMapping.has(key)) {
        const parent = FileMapping.get(key);
        if (file.isFolder) {
          parent.children.push(file);
        }
        parent.nodes.push(file);
        file.parentFolder = parent;
      }
    }
  }

  const mergedFolders = [...cloneFolders, ...systemFolders]

  return [
    root,
    id ? mergedFolders.find(item => item.id === id) : root,
    {
      files: cloneFiles,
      folders: mergedFolders
    }
  ]


  function makeEquipmentFolder(equipment) {
    if (!equipment || equipment.id === null || equipment.id === undefined) {
      return null
    }
    const { id: equipmentId } = equipment;
    // 目录标题统一走实体编号规范：EQ-{id}（与工单目录 WO-{id} 对齐）。
    // 注意 key / id 仍必须是 'equipment-{id}'，面包屑、收藏夹、MoveTo、
    // formatFilesToTreeData 的 id 还原都依赖它。
    const title = formatEquipmentRef(equipmentId);

    if (!equipmentFolderMappings[equipmentId]) {
      equipmentFolderMappings[equipmentId] = {
        isFolder: true,
        key: 'equipment-' + equipmentId,
        // 历史 bug：这里原本重复写了两遍 key 而从未赋值 id，导致设备目录
        // 在 MoveTo / 刷新还原 / 收藏夹里拿到 undefined。
        id: 'equipment-' + equipmentId,
        isSystem: true,
        title,
        name: title,
        children: [],
        nodes: [],
        context: {
          table_name: 'equipment',
          equipmentId,
          record_id: equipmentId,
          // R1：System 子树只读——设备虚拟目录下禁止新建/上传
          canCreate: false,
          canUpload: false
        }
      }
    }

    return equipmentFolderMappings[equipmentId]
  }

  function makeWorkOrderFolder(recordId) {
    if (recordId === null || recordId === undefined || String(recordId).trim() === '') {
      return null
    }
    if (!workOrderFolderMappings[recordId]) {
      const key = 'workorder-' + recordId;
      // 展示名走统一实体编号规范（#123 / 123 → WO-123，WO-0007 原样）
      const title = formatWorkorderRef(recordId);
      workOrderFolderMappings[recordId] = {
        isFolder: true,
        key,
        id: key,
        isSystem: true,
        title,
        name: title,
        children: [],
        nodes: [],
        context: {
          table_name: 'workorder',
          // 工单是多设备（M2M），虚拟目录不再绑定单一 equipmentId；
          // 该节点位于 System 子树内，一切写路径均被 R1 拦截。
          record_id: recordId,
          // R1：System 子树只读——workorder 虚拟目录下禁止新建/上传
          canCreate: false,
          canUpload: false
        }
      }
    }
    return workOrderFolderMappings[recordId]
  }

  function makeFunctionalTestingEquipmentFolder(equipment) {
    if (!equipment || equipment.id === null || equipment.id === undefined) {
      return null
    }
    const equipmentId = equipment.id;
    if (!functionalTestingEquipmentFolderMappings[equipmentId]) {
      const title = (equipment.name || 'Equipment') + '-' + equipmentId;
      functionalTestingEquipmentFolderMappings[equipmentId] = {
        isFolder: true,
        key: 'functionaltesting-equipment-' + equipmentId,
        id: 'functionaltesting-equipment-' + equipmentId,
        isSystem: true,
        title,
        name: title,
        children: [],
        nodes: [],
        context: {
          canCreate: false,
          canUpload: false
        }
      }
    }
    return functionalTestingEquipmentFolderMappings[equipmentId]
  }

  function makeFunctionalTestingFolder(equipment, recordId) {
    if (recordId === null || recordId === undefined || String(recordId).trim() === '') {
      return null
    }
    // FT 仍属单台设备，根下保留设备层；但绝不调用 makeEquipmentFolder，
    // 否则「只有功能测试附件的设备」会在 Equipment 根下多出一个空目录。
    if (equipment) {
      makeFunctionalTestingEquipmentFolder(equipment);
    }
    if (!functionalTestingFolderMappings[recordId]) {
      functionalTestingFolderMappings[recordId] = {
        isFolder: true,
        key: 'functional-' + recordId,
        id: 'functional-' + recordId,
        isSystem: true,
        title: String(recordId),
        name: String(recordId),
        children: [],
        nodes: [],
        context: {
          table_name: 'functiontesting',
          // 挂载时用它定位 FT 设备层；无设备则直接挂 FT 根
          equipmentId: equipment?.id ?? null,
          record_id: recordId,
          // R1：System 子树只读——功能测试虚拟目录下禁止新建/上传
          canCreate: false,
          canUpload: false
        }
      }
    }
    return functionalTestingFolderMappings[recordId]
  }

  // ---------------------------------------------------------------------------
  // ContractRequest（虚拟分组根 + 真实 Folder 回挂）
  //
  // 合同便签附件自 migration 1090 起就是真实 KpiFiles 行，目录
  //   System / ContractRequest / {id}-{title} / Announcement
  //                                          / Submission - {公司名}
  // 也是真实 Folder 记录（table_name='contract_request' 的是合同目录，其
  // announcement/submission 子目录通过 parent 自然成树）。这里只保留
  // ContractRequest 这个虚拟分组根：它没有任何对应数据行，只负责把分散的
  // 合同目录聚拢到 System 之下。目录显示名由后端 display_name 实时派生。
  // ---------------------------------------------------------------------------

  function makeSystemRelationShip(root) {

    const systemFolders = []

    // 统一挂载：同时维护 children（只放目录）、nodes（目录 + 文件）与
    // parentFolder 回溯指针（面包屑 getPathsByCurrentTree 依赖它）。
    const mount = (parentFolder, childFolder) => {
      if (!parentFolder || !childFolder) return;
      parentFolder.children.push(childFolder);
      parentFolder.nodes.push(childFolder);
      childFolder.parentFolder = parentFolder;
      systemFolders.push(childFolder);
    }

    const systemFolder = {
      isFolder: true,
      key: 'system',
      id: 'system',
      title: 'System',
      name: 'System',
      isSystem: true,
      children: [],
      nodes: [],
      context: {
        // R1：System 根目录本身也只读
        canCreate: false,
        canUpload: false
      }
    }

    const equipmentRootFolder = {
      isFolder: true,
      key: 'equipment',
      id: 'equipment',
      title: 'Equipment',
      name: 'Equipment',
      isSystem: true,
      children: [],
      nodes: [],
      context: {
        canCreate: false,
        canUpload: false
      }
    }

    // 工单已由「挂在设备下」改为与设备平级：Workorder 是 System 下的独立根，
    // 每个工单一个 WO-{record_id} 目录（多设备工单不再拆成多份）。
    const workOrderRootFolder = {
      isFolder: true,
      key: 'workorder',
      id: 'workorder',
      title: 'Workorder',
      name: 'Workorder',
      isSystem: true,
      children: [],
      nodes: [],
      context: {
        canCreate: false,
        canUpload: false
      }
    }

    // 功能测试同样提升为 System 平级根，但其内部保留设备层
    // （FT 记录仍归属单台设备）。
    const functionalTestingRootFolder = {
      isFolder: true,
      key: 'functionaltesting',
      id: 'functionaltesting',
      title: 'Functional Testing',
      name: 'Functional Testing',
      isSystem: true,
      children: [],
      nodes: [],
      context: {
        canCreate: false,
        canUpload: false
      }
    }

    systemFolders.push(root)

    mount(root, systemFolder);
    mount(systemFolder, equipmentRootFolder);
    mount(systemFolder, workOrderRootFolder);
    mount(systemFolder, functionalTestingRootFolder);

    for (const equipmentId of Object.keys(equipmentFolderMappings)) {
      mount(equipmentRootFolder, equipmentFolderMappings[equipmentId]);
    }

    for (const recordId of Object.keys(workOrderFolderMappings)) {
      mount(workOrderRootFolder, workOrderFolderMappings[recordId]);
    }

    // FT：先挂设备层，再把测试目录挂到各自设备层；无设备的直接挂 FT 根。
    for (const equipmentId of Object.keys(functionalTestingEquipmentFolderMappings)) {
      mount(functionalTestingRootFolder, functionalTestingEquipmentFolderMappings[equipmentId]);
    }
    for (const recordId of Object.keys(functionalTestingFolderMappings)) {
      const leafFolder = functionalTestingFolderMappings[recordId];
      const equipmentFolder = leafFolder?.context?.equipmentId != null
        ? functionalTestingEquipmentFolderMappings[leafFolder.context.equipmentId]
        : null;
      mount(equipmentFolder || functionalTestingRootFolder, leafFolder);
    }

    // 合同请求目录：有真实 contract_request 目录时才建虚拟分组根，
    // 真实目录在上方挂载循环里按 table_name 回挂进来。
    const contractRootFolders = cloneFolders.filter(
      item => item.table_name === 'contract_request' && !item.parent
    );
    if (contractRootFolders.length) {
      contractRequestRootNode = {
        isFolder: true,
        key: 'contractrequest',
        id: 'contractrequest',
        title: 'ContractRequest',
        name: 'ContractRequest',
        isSystem: true,
        isVirtual: true,
        children: [],
        nodes: [],
        context: {
          canCreate: false,
          canUpload: false
        }
      }
      mount(systemFolder, contractRequestRootNode);
    }

    return systemFolders
  }
}



export const filterFolders = (nodes) => {
  return nodes.filter(node => node.isFolder).map(node => ({
    ...node,
    children: node.children ? filterFolders(node.children) : undefined,
    nodes: node.children ?? []
  }));
};

export const getSafeUrl = (url = '') => {
  if (url.startsWith('https://facilitykpiclientfile.s3.us-east-2.amazonaws.com/facilitykpiclientfile')) {
    return url.slice(79)
  }
  return url
}

export const usePreviewUrl = (currentOpenFile) => {
  const [previewUrl, setPreviewUrl] = useSafeState();
  const [isFileViewLoading, setIsFileViewLoading] = useSafeState(false)

  const run = async () => {
    setIsFileViewLoading(true)
    let generate_url = await getFileManagementApi().generate_url(getSafeUrl(currentOpenFile.image), true).then(
      response => {
        let dataSource = response.data.url
        return dataSource
      }
    ).catch(error => (
      error
    )).finally(() => {
      setIsFileViewLoading(false)
    });
    setPreviewUrl(generate_url)
  }

  useEffect(() => {
    setPreviewUrl('')
    if (currentOpenFile?.image) {
      run()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOpenFile])

  return [currentOpenFile?.image ? previewUrl : '', isFileViewLoading];
}
const regex = /\/[^\/?#]+\.(\w+)(?=[?#]|$)/i

const getExtensionByUrl = (url = '') => {
  return url.match(regex)?.[1] ?? ''
}

const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
export const isImage = (fileName = '') => {
  const extension = getExtensionByUrl(fileName);

  if (imageExtensions.includes(extension)) {
    return true;
  }
  return false
}

const docExtensions = [
  "doc",    // Word 97-2003 文档
  "docx",   // Microsoft Word 文档
  "ppt",    // PowerPoint 演示文稿
  "pptx",   // Microsoft PowerPoint 演示文稿
  "xls",    // Excel 97-2003 工作簿
  "xlsx"    // Microsoft Excel 工作簿
];

export const isDoc = (fileName = '') => {

  const extension = getExtensionByUrl(fileName);
  if (docExtensions.includes(extension)) {
    return true;
  }
  return false
}

const mediaExtensions = ['mp4', 'mov', 'avi', 'flv', 'rmvb', 'wmv']

export const isMedia = (fileName = '') => {

  const extension = getExtensionByUrl(fileName);

  if (mediaExtensions.includes(extension)) {
    return true;
  }
  return false
}

const audioExtensions = ['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma'];
export const isAudio = (fileName = '') => {
  const extension = getExtensionByUrl(fileName);

  if (audioExtensions.includes(extension)) {
    return true;
  }
  return false
}


const supportIframeExtensions = ['pdf', 'text', 'json', 'html', 'txt'];

export const isSupportIframe = (fileName = '') => {
  const extension = getExtensionByUrl(fileName);

  if (supportIframeExtensions.includes(extension)) {
    return true;
  }
  return false
}

export const tablelocale = (isResultMode, isEntityFilter = false) => ({
  emptyText: (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        isEntityFilter
          ? 'No files found for this filter.'
          : isResultMode
          ? 'No files found.'
          : 'This folder is empty.'
      }
    />
  )
});

const suportOpenAppFiles = ['xls', 'xlsx', 'ppt', 'pptx', 'doc', 'docx', 'pdf'];

export const canOpenInApp = (record) => {
  if (record.isFolder) {
    return false
  }
  return suportOpenAppFiles.includes(record.name.split('.').pop())
}

// Copy Link
export const copyLink = async (record) => {
  getFileManagementApi().generate_url(getSafeUrl(record.image), true).then(
    response => {
      let url = response.data.url
      navigator.clipboard.writeText(url);
      message.success('Link copied to clipboard');
    }
  )
}

export const handleDownload = async (record) => {

  const { image: downloadUrl, name } = record;

  const generate_url = await getFileManagementApi().generate_url(getSafeUrl(downloadUrl), false).then(
    response => {
      const url = response.data.url;
      return url
    })

  const link = document.createElement('a');
  link.href = generate_url;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link)
}


export const openInBrowser = async (record) => {
  const { image: downloadUrl } = record;

  const generate_url = await getFileManagementApi().generate_url(getSafeUrl(downloadUrl), false).then(
    response => {
      const url = response.data.url;
      return url
    })

  const googleDocsUrl = 'https://docs.google.com/viewer?url=' + encodeURIComponent(generate_url) + '&embedded=true';
  window.open(googleDocsUrl, '_blank');
}

export const openInApp = async (record) => {
  const { image: downloadUrl } = record;
  const url = await getFileManagementApi().generate_url(getSafeUrl(downloadUrl), false).then(
    response => {
      const url = response.data.url;
      return url
    })
    openAppByUrl(url)
}

export const openAppByUrl = async (url) => {
  const aEl = document.createElement('a');
  switch (getExtensionByUrl(url)) {
    case 'xlsx':
    case 'xls':
      aEl.href = `ms-excel:ofv|u|${url}`
      break;
    case 'pptx':
    case 'ppt':
      aEl.href = `ms-powerpoint:ofv|u|${url}`
      break;
    case 'docx':
    case 'doc':
      aEl.href = `ms-word:ofv|u|${url}`
      break;
    case 'pdf':
      aEl.href = `ms-pdf:ofv|u|${url}`
      break;
    default:
      return
  }
  document.body.appendChild(aEl);
  aEl.click();
  document.body.removeChild(aEl);
}

export const beforeUpload = (file) => {
  const isLessThan20M = file.size / 1024 / 1024 < 20;
  if (!isLessThan20M) {
    message.error('File must be less than 20MB');
    return Upload.LIST_IGNORE;
  }
  return false;
}


export const allowedFileTypes = [
  ...imageExtensions,
  ...docExtensions,
  ...mediaExtensions,
  ...audioExtensions,
  ...supportIframeExtensions
]